'use client';
import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  Eye, EyeOff, Globe2, CheckCircle2, Shield, Sparkles,
  ArrowRight, Loader2, User, Building2
} from 'lucide-react';

type AuthMode = 'login' | 'signup';
type UserRole = 'individual' | 'agency';

interface LoginForm {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface SignupForm {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agencyName?: string;
  agreeTerms: boolean;
}

const demoCredentials = [
  { role: 'Individual', email: 'layla.rashidi@gmail.com', password: 'VisaDemo2026!' },
  { role: 'Agency', email: 'sara.kowalski@travelpro.pl', password: 'AgencyDemo2026!' },
];

const trustPoints = [
  { icon: Shield, text: 'AES-256 document encryption' },
  { icon: Globe2, text: '180+ countries covered' },
  { icon: CheckCircle2, text: '98.2% visa approval rate' },
  { icon: Sparkles, text: 'AI-generated in under 8 minutes' },
];

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [role, setRole] = useState<UserRole>('individual');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();

  const loginForm = useForm<LoginForm>({ defaultValues: { rememberMe: false } });
  const signupForm = useForm<SignupForm>({ defaultValues: { agreeTerms: false } });

  const handleLogin = async (data: LoginForm) => {
    setIsLoading(true);
    // TODO: Connect to Supabase auth or Node.js /api/auth/login
    await new Promise((r) => setTimeout(r, 1400));

    const validCredentials = demoCredentials.find(
      (c) => c.email === data.email && c.password === data.password
    );
    if (!validCredentials) {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
      setIsLoading(false);
      return;
    }
    toast.success(`Welcome back! Redirecting to your dashboard...`);
    setTimeout(() => router.push('/visa-application-wizard'), 1000);
    setIsLoading(false);
  };

  const handleSignup = async (data: SignupForm) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    // TODO: Connect to Supabase auth or Node.js /api/auth/register
    await new Promise((r) => setTimeout(r, 1600));
    toast.success('Account created! Starting your first application...');
    setTimeout(() => router.push('/visa-application-wizard'), 1000);
    setIsLoading(false);
  };

  const fillCredentials = (email: string, password: string) => {
    loginForm.setValue('email', email);
    loginForm.setValue('password', password);
    toast.info('Demo credentials filled — click Sign in to continue');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left: Brand panel */}
      <div className="hidden lg:flex lg:w-2/5 xl:w-1/2 bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 relative overflow-hidden flex-col justify-between p-10">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-white/5 rounded-full blur-3xl" />
          <div className="absolute bottom-20 -left-20 w-96 h-96 bg-sky-400/10 rounded-full blur-3xl" />
        </div>

        {/* Logo */}
        <div className="relative flex items-center gap-2.5">
          <AppLogo size={36} />
          <span className="font-bold text-xl text-white tracking-tight">VisaPilot</span>
          <span className="text-xs font-bold bg-white/20 text-white/90 px-1.5 py-0.5 rounded">AI</span>
        </div>

        {/* Center content */}
        <div className="relative space-y-8">
          <div className="space-y-3">
            <h2 className="text-3xl xl:text-4xl font-extrabold text-white leading-tight tracking-tight">
              Your complete visa package,
              <br />
              <span className="text-sky-200">ready in minutes.</span>
            </h2>
            <p className="text-blue-100 text-base leading-relaxed">
              AI-generated checklists, cover letters, and document validation — tailored to your destination and visa type.
            </p>
          </div>

          {/* Trust points */}
          <div className="space-y-3">
            {trustPoints.map((tp) => (
              <div key={`trust-${tp.text}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
                  <tp.icon size={15} className="text-sky-200" />
                </div>
                <span className="text-sm text-blue-100 font-medium">{tp.text}</span>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { value: '52K+', label: 'Packages' },
              { value: '98.2%', label: 'Approval' },
              { value: '180+', label: 'Countries' },
            ].map((stat) => (
              <div key={`stat-${stat.label}`} className="text-center">
                <p className="text-2xl font-extrabold text-white tabular-nums">{stat.value}</p>
                <p className="text-xs text-blue-200 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom language flags */}
        <div className="relative flex items-center gap-2">
          <span className="text-xs text-blue-300 font-medium">Available in:</span>
          {['🇺🇸 EN', '🇸🇦 AR', '🇵🇰 UR', '🇮🇳 HI'].map((lang) => (
            <span key={`lang-flag-${lang}`} className="text-xs bg-white/10 text-white px-2 py-0.5 rounded-full font-medium">
              {lang}
            </span>
          ))}
        </div>
      </div>

      {/* Right: Auth form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-12 xl:px-16 bg-white">
        <div className="max-w-md w-full mx-auto space-y-7">
          {/* Mobile logo */}
          <div className="flex lg:hidden items-center gap-2 mb-2">
            <AppLogo size={30} />
            <span className="font-bold text-lg text-gray-900">VisaPilot AI</span>
          </div>

          {/* Mode toggle */}
          <div>
            <div className="flex bg-gray-100 rounded-xl p-1 gap-1 w-full">
              {(['login', 'signup'] as AuthMode[]).map((m) => (
                <button
                  key={`mode-${m}`}
                  onClick={() => setMode(m)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    mode === m ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {m === 'login' ? 'Sign in' : 'Create account'}
                </button>
              ))}
            </div>
          </div>

          {/* Role selector */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
              I am applying as
            </label>
            <div className="grid grid-cols-2 gap-2">
              {([
                { value: 'individual', label: 'Individual', sub: 'Traveler / student / worker', icon: User },
                { value: 'agency', label: 'Agency', sub: 'Consultant / travel agency', icon: Building2 },
              ] as const).map((r) => (
                <button
                  key={`role-${r.value}`}
                  onClick={() => setRole(r.value)}
                  className={`flex items-center gap-2.5 p-3 rounded-xl border text-left transition-all duration-150 ${
                    role === r.value
                      ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' :'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    role === r.value ? 'bg-blue-600' : 'bg-gray-100'
                  }`}>
                    <r.icon size={15} className={role === r.value ? 'text-white' : 'text-gray-500'} />
                  </div>
                  <div>
                    <p className={`text-xs font-bold ${role === r.value ? 'text-blue-800' : 'text-gray-700'}`}>{r.label}</p>
                    <p className="text-xs text-gray-400">{r.sub}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Social auth */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Google', color: 'hover:bg-red-50 hover:border-red-200' },
              { label: 'LinkedIn', color: 'hover:bg-blue-50 hover:border-blue-200' },
            ].map((social) => (
              <button
                key={`social-${social.label}`}
                className={`flex items-center justify-center gap-2 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 bg-white transition-all duration-150 active:scale-95 ${social.color}`}
              >
                {social.label}
              </button>
            ))}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-100" />
            <span className="text-xs text-gray-400 font-medium">or continue with email</span>
            <div className="flex-1 h-px bg-gray-100" />
          </div>

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4 animate-fade-in">
              {/* Email */}
              <div className="space-y-1.5">
                <label htmlFor="login-email" className="text-sm font-semibold text-gray-700">
                  Email address
                </label>
                <input
                  id="login-email"
                  type="email"
                  placeholder="you@example.com"
                  className="input-field"
                  {...loginForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
                  })}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-red-500 font-medium">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label htmlFor="login-password" className="text-sm font-semibold text-gray-700">
                    Password
                  </label>
                  <a href="#" className="text-xs text-blue-600 font-semibold hover:underline">
                    Forgot password?
                  </a>
                </div>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    className="input-field pr-11"
                    {...loginForm.register('password', { required: 'Password is required' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-red-500 font-medium">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              {/* Remember me */}
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  {...loginForm.register('rememberMe')}
                />
                <span className="text-sm text-gray-600 group-hover:text-gray-800 transition-colors">
                  Remember me for 30 days
                </span>
              </label>

              {/* Submit */}
              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full justify-center py-3 text-sm"
              >
                {isLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>Sign in to VisaPilot <ArrowRight size={15} /></>
                )}
              </button>
            </form>
          )}

          {/* Signup Form */}
          {mode === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4 animate-fade-in">
              {/* Full name */}
              <div className="space-y-1.5">
                <label htmlFor="signup-name" className="text-sm font-semibold text-gray-700">
                  Full name
                </label>
                <input
                  id="signup-name"
                  type="text"
                  placeholder="Layla Al-Rashidi"
                  className="input-field"
                  {...signupForm.register('fullName', { required: 'Full name is required' })}
                />
                {signupForm.formState.errors.fullName && (
                  <p className="text-xs text-red-500 font-medium">{signupForm.formState.errors.fullName.message}</p>
                )}
              </div>

              {/* Agency name (conditional) */}
              {role === 'agency' && (
                <div className="space-y-1.5 animate-fade-in">
                  <label htmlFor="signup-agency" className="text-sm font-semibold text-gray-700">
                    Agency / company name
                  </label>
                  <input
                    id="signup-agency"
                    type="text"
                    placeholder="TravelPro Consultancy"
                    className="input-field"
                    {...signupForm.register('agencyName', {
                      required: role === 'agency' ? 'Agency name is required' : false,
                    })}
                  />
                  {signupForm.formState.errors.agencyName && (
                    <p className="text-xs text-red-500 font-medium">{signupForm.formState.errors.agencyName.message}</p>
                  )}
                </div>
              )}

              {/* Email */}
              <div className="space-y-1.5">
                <label htmlFor="signup-email" className="text-sm font-semibold text-gray-700">
                  Email address
                </label>
                <input
                  id="signup-email"
                  type="email"
                  placeholder="you@example.com"
                  className="input-field"
                  {...signupForm.register('email', {
                    required: 'Email is required',
                    pattern: { value: /\S+@\S+\.\S+/, message: 'Enter a valid email' },
                  })}
                />
                {signupForm.formState.errors.email && (
                  <p className="text-xs text-red-500 font-medium">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <label htmlFor="signup-password" className="text-sm font-semibold text-gray-700">
                  Password
                </label>
                <p className="text-xs text-gray-400">Minimum 8 characters, including a number and symbol</p>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    className="input-field pr-11"
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-xs text-red-500 font-medium">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              {/* Confirm password */}
              <div className="space-y-1.5">
                <label htmlFor="signup-confirm" className="text-sm font-semibold text-gray-700">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    id="signup-confirm"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Repeat your password"
                    className="input-field pr-11"
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Toggle confirm password"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-red-500 font-medium">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              {/* Terms */}
              <div className="space-y-1">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 rounded border-gray-300 text-blue-600"
                    {...signupForm.register('agreeTerms', { required: 'You must agree to the terms' })}
                  />
                  <span className="text-sm text-gray-600">
                    I agree to the{' '}
                    <a href="#" className="text-blue-600 font-semibold hover:underline">Terms of Service</a>
                    {' '}and{' '}
                    <a href="#" className="text-blue-600 font-semibold hover:underline">Privacy Policy</a>
                  </span>
                </label>
                {signupForm.formState.errors.agreeTerms && (
                  <p className="text-xs text-red-500 font-medium">{signupForm.formState.errors.agreeTerms.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn-primary w-full justify-center py-3 text-sm"
              >
                {isLoading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <>Create account <ArrowRight size={15} /></>
                )}
              </button>
            </form>
          )}

          {/* Demo credentials box */}
          {mode === 'login' && (
            <div className="border border-blue-100 bg-blue-50/60 rounded-xl p-4 space-y-3 animate-fade-in">
              <p className="text-xs font-bold text-blue-700 uppercase tracking-widest">Demo accounts</p>
              <div className="space-y-2">
                {demoCredentials.map((cred) => (
                  <div key={`cred-${cred.role}`} className="flex items-center justify-between gap-2 bg-white rounded-lg px-3 py-2 border border-blue-100">
                    <div>
                      <span className="text-xs font-bold text-blue-800">{cred.role}</span>
                      <p className="text-xs text-gray-500 font-mono">{cred.email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => fillCredentials(cred.email, cred.password)}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-lg transition-colors duration-150"
                    >
                      Use →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Encryption notice */}
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
            <Shield size={14} className="text-green-500 shrink-0" />
            <p className="text-xs text-gray-500">
              Your documents are encrypted with AES-256. We never share your data with third parties.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}